import { DashboardLayout } from '@/components/layouts/DashboardLayout';
import { MemberPlan, PlanComparison } from '@/components/pricing/PlanComparison';
import { Badge } from '@/components/ui/badge';
import { SubscriptionInfo, useApiClient } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { getUserErrorMessage } from '@/lib/error-messages';
import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

export default function Subscription() {
  const { api } = useApiClient();
  const { refreshAuthSnapshot, user } = useAuth();
  const query = useQuery();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sub, setSub] = useState<SubscriptionInfo | null>(null);
  const [showOneWeekOffer] = useState(user?.one_week_offer_eligible === true);

  const [startingCheckout, setStartingCheckout] = useState(false);
  const [startingWeeklyCheckout, setStartingWeeklyCheckout] = useState(false);
  const [startingQuarterlyCheckout, setStartingQuarterlyCheckout] = useState(false);

  const status = query.get('status');

  const refresh = async () => {
    setError(null);
    try {
      const res = await api.getMySubscription();
      setSub(res.subscription || null);
    } catch (e: any) {
      setError(
        getUserErrorMessage(
          e,
          'Unable to load subscription details right now. Please try again later.'
        )
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // If returning from checkout success, refresh auth snapshot to embed subscription into stored user
    if (status === 'success') {
      // Fire and forget
      void refreshAuthSnapshot();
    }
    if (showOneWeekOffer) {
      void api.markOneWeekOfferSeen().then(() => refreshAuthSnapshot()).catch(() => undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleStartCheckout = async () => {
    setError(null);
    try {
      setStartingCheckout(true);
      const res = await api.createSubscriptionCheckout('monthly');
      if (res.checkout_url) {
        window.location.href = res.checkout_url;
      } else {
        setError('Failed to start checkout.');
      }
    } catch (e: any) {
      setError(
        getUserErrorMessage(
          e,
          'Unable to start checkout right now. Please try again later.'
        )
      );
    } finally {
      setStartingCheckout(false);
    }
  };

  const handleStartWeeklyCheckout = async () => {
    setError(null);
    try {
      setStartingWeeklyCheckout(true);
      const res = await api.createSubscriptionCheckout('weekly');
      if (res.checkout_url) {
        window.location.href = res.checkout_url;
      } else {
        setError('Failed to start one-week checkout.');
      }
    } catch (e: any) {
      setError(
        getUserErrorMessage(
          e,
          'Unable to start one-week checkout right now. Please try again later.'
        )
      );
    } finally {
      setStartingWeeklyCheckout(false);
    }
  };

  const handleStartQuarterlyCheckout = async () => {
    setError(null);
    try {
      setStartingQuarterlyCheckout(true);
      const res = await api.createSubscriptionCheckout('quarterly');
      if (res.checkout_url) {
        window.location.href = res.checkout_url;
      } else {
        setError('Failed to start three-month checkout.');
      }
    } catch (e: any) {
      setError(
        getUserErrorMessage(
          e,
          'Unable to start checkout right now. Please try again later.'
        )
      );
    } finally {
      setStartingQuarterlyCheckout(false);
    }
  };

  const currentPlan = useMemo<MemberPlan>(() => {
    const now = new Date();
    const expires = sub?.current_period_end ? new Date(sub.current_period_end) : null;
    const isActive = Boolean(sub && expires && expires.getTime() > now.getTime());

    if (!isActive) return 'free';
    if (sub?.price_id?.includes('quarter')) return 'quarterly';
    if (sub?.price_id?.includes('week')) return 'weekly';
    return 'monthly';
  }, [sub]);

  const successAlert = status === 'success' ? (
    <div className="rounded-md bg-emerald-50 p-4 border border-emerald-200">
      <div className="flex items-start gap-3">
        <span className="mt-0.5"><Badge className="bg-emerald-600">Success</Badge></span>
        <div>
          <p className="text-sm font-medium text-emerald-800">Plan activated</p>
          <p className="mt-1 text-sm text-emerald-700">Checkout completed. Your plan window is now active as a one-time purchase.</p>
        </div>
      </div>
    </div>
  ) : status === 'cancel' ? (
    <div className="rounded-md bg-amber-50 p-4 border border-amber-200">
      <div className="flex items-start gap-3">
        <span className="mt-0.5"><Badge className="bg-amber-600">Canceled</Badge></span>
        <div>
          <p className="text-sm font-medium text-amber-800">Checkout canceled</p>
          <p className="mt-1 text-sm text-amber-700">You can restart checkout at any time below.</p>
        </div>
      </div>
    </div>
  ) : null;

  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto py-6">

        {successAlert}

        {error && (
          <div className="mt-4 rounded-md bg-rose-50 p-4 border border-rose-200">
            <p className="text-sm font-medium text-rose-800">{error}</p>
          </div>
        )}

        {/* Plan comparison grid */}
        <div className="mt-10">
          <PlanComparison
            currentPlan={currentPlan}
            showWeeklyOffer={showOneWeekOffer}
            onSelectWeekly={handleStartWeeklyCheckout}
            onSelectMonthly={handleStartCheckout}
            onSelectQuarterly={handleStartQuarterlyCheckout}
            weeklyBusy={startingWeeklyCheckout}
            monthlyBusy={startingCheckout}
            quarterlyBusy={startingQuarterlyCheckout}
          />
        </div>
      </div>
    </DashboardLayout>
  );
}
